import {
  AfterViewInit,
  OnInit,
  Component,
  ViewChild,
  Inject,
  ElementRef,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { KeycloakService } from "keycloak-angular";
import { Observable } from "rxjs";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import {
  MatSlideToggleChange,
  MatSlideToggleModule,
} from "@angular/material/slide-toggle";
import { MatSort, MatSortModule } from "@angular/material/sort";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { FormsModule } from '@angular/forms';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import {
  UserService,
  User,
  UserRole,
} from "@kelyn-technologies/dsio-angular-lib/dsio-api";

@Component({
    selector: "app-user-management",
    imports: [
        MatButtonModule,
        MatTableModule,
        MatSlideToggleModule,
        MatSortModule,
        CommonModule,
        MatProgressBarModule,
        MatDialogModule,
        MatSnackBarModule,
    ],
    templateUrl: "./user-management.component.html",
    styleUrls: ["./user-management.component.css"]
})
export class UserManagementComponent implements AfterViewInit, OnInit {
  isloading: boolean = true;

  tenantId: string = sessionStorage.getItem("tenantId") as string;
  isLoggedIn = false;
  USER_DATA: User[] = [];
  displayedColumns: string[] = [
    "username",
    "email_address",
    "last_name",
    "tenant_admin",
  ];
  dataSource = new MatTableDataSource<User>([]);

  editUserId: string = "";
  editUsername: string = "";
  editEmailAddress: string = "";
  editFirstName: string = "";
  editLastName: string = "";
  editIsAdmin: boolean = false;

  currentUserId: string = "";
  currentUsername: string = "";

  public selectedRow: string = "";
  // constructor(public dialog: MatDialog){}
  constructor(
    private userService: UserService,
    public dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private keycloak: KeycloakService,
  ) {}

  @ViewChild(MatSort) sort!: MatSort;

  async ngOnInit(): Promise<void> {
    const loggedInUser = (this.keycloak.getKeycloakInstance().tokenParsed as any) || {};
    this.currentUserId = loggedInUser.sub || '';
    this.currentUsername = (loggedInUser.preferred_username || '').toLowerCase();

    this.handleGetUsers();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  onAdminToggle(user: User, nextValue: boolean) {
    const prev = user.tenant_admin ?? false;
    user.tenant_admin = nextValue;
    this.dataSource.data = [...this.USER_DATA];

    const role: UserRole = { tenant_admin: nextValue };

    this.userService.updateUserRoles(user.user_id, role).subscribe({
    next: () => this._snackBar.open('Role updated', 'close'),
      error: (err) => {
        user.tenant_admin = prev;
        this.dataSource.data = [...this.USER_DATA];
        const msg = err?.message || err?.statusText || 'Failed to update role';
        this._snackBar.open(msg, 'close');
      }
    });
  }

  setSelected(row: User): void {
    this.selectedRow = row.user_id ?? '';
    this.editUserId = row.user_id ? row.user_id : "";
    this.editUsername = row.username ? row.username : "";
    this.editEmailAddress = row.email_address ? row.email_address : "";
    this.editFirstName = row.first_name ? row.first_name : "";
    this.editLastName = row.last_name ? row.last_name : "";
    this.editIsAdmin = row.tenant_admin as boolean;
  }

  getSelected(userId: string): User {
    let user: User = {
      user_id: "",
      username: "",
      email_address: "",
      first_name: "",
      last_name: "",
      tenant_admin: false,
    };
    this.USER_DATA.forEach((row: User) => {
      if (row.user_id === userId) {
        user = row;
      }
    });
    return user;
  }

  handleAddNewUserDialog(): void {
    const dialogRef = this.dialog.open(AddUserDialog, {
      width: "342px",
      data: {
        userId: "",
        username: "",
        email_address: "",
        first_name: "",
        last_name: "",
        tenant_admin: false,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.handleAddUser(result);
    });
  }

  handleEditUserDialog(userId: string): void {
    const dialogRef = this.dialog.open(EditUserDialog, {
      width: "342px",
      data: {
        user_id: this.editUserId,
        username: this.editUsername,
        email_address: this.editEmailAddress,
        first_name: this.editFirstName,
        last_name: this.editLastName,
        tenant_admin: this.editIsAdmin,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.handleEditUser(result);
      this.handleUpdateRole(result);

      this.USER_DATA.forEach((row) => {
        if (row.user_id == result.user_id) {
          row.username = result.username ?? row.username;
          row.email_address = result.email_address ?? row.email_address;
          row.first_name = result.first_name ?? row.first_name;
          row.last_name = result.last_name ?? row.last_name;
          row.tenant_admin = result.tenant_admin ?? row.tenant_admin;
        }
      });
      this.dataSource.data = this.USER_DATA;
    });
  }

  isCurrentUser(user: User | undefined | null): boolean {
    if (!user) return false;
    if (this.currentUserId && user.user_id === this.currentUserId) return true;
    return !!this.currentUsername && user.username.toLowerCase() === this.currentUsername;
  }

  handleDeleteUserDialog(): void {
    const user = this.getSelected(this.selectedRow);

    if (!user?.user_id) {
      this._snackBar.open('Select a user first', 'close');
      return;
    }

    if (this.isCurrentUser(user)) {
      this._snackBar.open("You cannot delete your own account", "close");
      return;
    }

    const dialogRef = this.dialog.open(DeleteUserDialog, {
      width: "342px",
      data: user,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.handleDeleteUser(user.user_id);
      }
    });
  }

  async handleAddUser(user: User): Promise<void> {
    this.userService.createUser(user).subscribe(() => {
      this._snackBar.open('User added', 'close');
      this.handleGetUsers();
    });
  }

  async handleEditUser(data: User): Promise<void> {
    const userId = data.user_id;

    const request: User = {
      user_id: data.user_id,
      username: data.username,
      first_name: data.first_name,
      last_name: data.last_name,
      email_address: data.email_address,
      tenant_admin: !!data.tenant_admin,
    };

    this.userService.updateUser(userId, request).subscribe(() => {
      this._snackBar.open('User updated', 'close');
    });
  }

  async handleUpdateRole(user: User): Promise<void> {
    const role: UserRole = {
      tenant_admin: user.tenant_admin,
    };
    this.userService
      .updateUserRoles(user.user_id, role)
      .subscribe(() => {
        this._snackBar.open("Role Updated", "close");
        // console.log('Patch Call Successful', data);
      });
  }

  async handleDeleteUser(userId: string): Promise<void> {
    this.userService.deleteUser(userId).subscribe({
      next: () => {
        this._snackBar.open("User Deleted", "close");
        this.USER_DATA = this.USER_DATA.filter(u => u.user_id !== userId);
        this.dataSource.data = this.USER_DATA;
        if (this.selectedRow === userId) this.selectedRow = "";
      },
      error: (err) => {
        console.error('Delete failed', err);
        const msg = err?.message || err?.statusText || 'Delete failed';
        this._snackBar.open(msg, 'close');
      }
    });
  }

  async handleGetUsers() {
    this.isloading = true;
    if (this.USER_DATA.length > 0) {
      this.USER_DATA = [];
    }
    const request = this.userService.getUsers();
    request.subscribe((data: any) => {
      this.isloading = false;

      data.forEach(async (user: User, index: number) => {
        const newuser = {
          user_id: user.user_id,
          username: user.username,
          email_address: user.email_address,
          first_name: user.first_name,
          last_name: user.last_name,
          tenant_admin: user.tenant_admin,
        };
        this.USER_DATA.push(newuser);
      });
      this.dataSource.data = this.USER_DATA;
    });
  }
}

@Component({
    selector: "dsio-add-user-dialog",
    templateUrl: "./dsio-add-user-dialog.html",
    styleUrls: ["./user-management.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatSlideToggleModule,
        FormsModule,
    ]
})
export class AddUserDialog {
  @ViewChild("usernameInput") usernameInput!: ElementRef;
  @ViewChild("emailInput") emailInput!: ElementRef;
  @ViewChild("firstNameInput") firstNameInput!: ElementRef;
  @ViewChild("lastNameInput") lastNameInput!: ElementRef;
  constructor(
    public dialogRef: MatDialogRef<AddUserDialog>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  addUser(data: any): void {
    data.username = this.usernameInput.nativeElement.value;
    data.first_name = this.firstNameInput.nativeElement.value;
    data.last_name = this.lastNameInput.nativeElement.value;
    data.email_address = this.emailInput.nativeElement.value;
    data.tenant_admin = this.data.tenant_admin;
    this.dialogRef.close(data);
  }

}

@Component({
    selector: "dsio-edit-user-dialog",
    templateUrl: "./dsio-edit-user-dialog.html",
    styleUrls: ["./user-management.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatSlideToggleModule,
        FormsModule,
    ]
})
export class EditUserDialog {
  role$!: Observable<any>;
  @ViewChild("usernameInput") usernameInput!: ElementRef;
  @ViewChild("emailInput") emailInput!: ElementRef;
  @ViewChild("firstNameInput") firstNameInput!: ElementRef;
  @ViewChild("lastNameInput") lastNameInput!: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<EditUserDialog>,
    @Inject(MAT_DIALOG_DATA) public data: User
  ) {}

  ngOnInit(): void {
    this.data.tenant_admin = this.toBoolean(this.data.tenant_admin);
  }

  private toBoolean(v: any): boolean {
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  async updateUser(data: any): Promise<void> {
    data.first_name = this.firstNameInput.nativeElement.value;
    data.last_name = this.lastNameInput.nativeElement.value;
    data.email_address = this.emailInput.nativeElement.value;

    this.dialogRef.close(data);
  }

  updateAdmin(data: any): void {
    this.data.tenant_admin = !this.data.tenant_admin;
  }
}

@Component({
    selector: "dsio-delete-user-dialog",
    templateUrl: "./dsio-delete-user-dialog.html",
    styleUrls: ["./user-management.component.css"],
    imports: [
        MatButtonModule,
        MatDialogModule,
        CommonModule,
        MatSelectModule,
        MatInputModule,
        MatFormFieldModule,
        MatSlideToggleModule,
    ]
})
export class DeleteUserDialog {
  role$!: Observable<any>;

  constructor(
    public dialogRef: MatDialogRef<DeleteUserDialog>,
    @Inject(MAT_DIALOG_DATA) public data: User
  ){}

  cancel(): void {
    this.dialogRef.close(false);
  }

  confirmDelete(): void {
    this.dialogRef.close(true);
  }
}
